import { Box } from "@mui/material";
import { type FC, useMemo } from "react";
import { noLocationString } from "../../components/DayViewGaps/utils";
import { configuredAmountRequired } from "../../utils/censusResolution";
import type { RecurringShift, TimeOffRequest } from "../../utils/models";
import { getRowGridSx } from "../MasterSchedule/v2/utils";
import { useLocationCredentialsQueryHook } from "../Profile/v2/SynergyCredentials/credentialsHooks";
import { SynergyLocationCell } from "./Cells/SynergyLocationCell";
import { SynergyNurseCell } from "./Cells/SynergyNurseCell";
import { SynergyVisitsCell } from "./Cells/SynergyVisitsCell";
import { type LocationRowData, unassignedLocation } from "./utils";

type Props = {
	rowData: LocationRowData;
	shiftsAtLocation?: RecurringShift[];
	refreshRecurringShifts: () => Promise<void>;
	uniqueSections: string[];
	timeOffRequests: TimeOffRequest[];
	refetchDayOffRequests?: () => Promise<void>;
	refetchDayOffRequestsLoading?: boolean;
	selectedNurseId?: number | null;
};

export const SynergyStaffingRow: FC<Props> = ({
	rowData,
	shiftsAtLocation,
	refreshRecurringShifts,
	uniqueSections,
	timeOffRequests,
	refetchDayOffRequests,
	refetchDayOffRequestsLoading,
	selectedNurseId,
}) => {
	const { location, weekData } = rowData;

	const { credentialedNurseIds } = useLocationCredentialsQueryHook({
		locationId: location.id && location.id !== unassignedLocation.id ? location.id : null,
		hideExpired: true,
		onlyShowApproved: true,
	});

	const targetVisits = useMemo(() => {
		return weekData.reduce((sum, dayData) => {
			const dayAbbr = dayData.date.setLocale("en").toFormat("ccc").toLowerCase();
			// Synergy shows CONFIGURED targets, never census-derived ones — hence the explicit
			// configured-amount call rather than the census resolver.
			return sum + configuredAmountRequired(location, dayAbbr);
		}, 0);
	}, [weekData, location]);

	return (
		<Box sx={getRowGridSx(1, false, true, false)}>
			<SynergyLocationCell
				location={location}
				isLocationOutsideOfConfig={!location.location || location.location === noLocationString}
				shiftsAtLocation={shiftsAtLocation}
				refreshRecurringShifts={refreshRecurringShifts}
				uniqueSections={uniqueSections}
			/>
			<SynergyVisitsCell weekData={weekData} targetVisits={targetVisits} />

			{/* 7 Day Cells */}
			{weekData.map((dayData) => (
				<SynergyNurseCell
					key={dayData.dayIndex}
					nurses={dayData.nurses}
					date={dayData.date}
					facility={location}
					credentialedNurseIds={credentialedNurseIds}
					timeOffRequests={timeOffRequests}
					refetchDayOffRequests={refetchDayOffRequests}
					refetchDayOffRequestsLoading={refetchDayOffRequestsLoading}
					selectedNurseId={selectedNurseId}
				/>
			))}
		</Box>
	);
};
